/**
 * The Equipment step's identify card host (`equipment-page-identify`, R1–R18), and its guesses'
 * confirm (`equipment-page-confirm`, R4–R12).
 *
 * Owns which group the card is open on, the *Identify* button in each group's header, the close
 * rules, moving to the next group, the *Change* / *Confirm* buttons on a group to confirm, the
 * selected group and *Confirm All* in the panel bar, and binding the shared card and its writes (`IdentifyCard`,
 * `useIdentifyGroup` in `@repo/ui`) to this page's services. A render prop, because the button
 * goes into the Groups view and the card beside the main panel — two places in `EquipmentStage`:
 *
 *     <EquipmentIdentify …>{({ renderGroupActions, openGroupKey, dock, barActions }) => <EquipmentStage … />}</EquipmentIdentify>
 *
 * Rendered only on the Equipment step, so the toasts it raises (Undo) live as long as the step and
 * no longer — not as long as the card, which closes after the last group while its Undo is still
 * offered.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Icon } from '@repo/design-system';
import {
  IdentifyCard,
  useIdentifyGroup,
  useNotTrackedAction,
  type MarkNotTracked,
  type IdentifyBatchAnswer,
  type IdentifyChoice,
  type IdentifyGroupApi,
  type IdentifyPickerFetchers,
} from '@repo/ui';
import {
  GENERIC_CATALOGUE_MANUFACTURER,
  isGenericCatalogueManufacturer,
  nextGroupToIdentify,
  opensIdentifyCard,
  projectedScore,
  suggestsNotTracked,
  type IdentifyGroup,
  type IdentifyModel,
} from '@repo/front-end-utils';
import {
  findManufacturerCatalog,
  getManufacturerProductsByManufacturer,
  getRankedCandidates,
  identifyTwinAssets,
  refreshTwinAssetProposals,
  undoIdentifyTwinAssets,
} from '@repo/front-end-services';
import { useEquipmentCatalogStore } from '@repo/front-end-state';
import type { ManufacturerHierarchy } from '@repo/frontend-types';

import { guessMaker, guessText, type EquipmentChip } from './equipmentFilter';

/** Stands in while there is no model, so the hooks below run the same way every render. */
const EMPTY_MODEL: IdentifyModel = {
  groups: [],
  stateOf: () => undefined,
} as unknown as IdentifyModel;

/** How many catalogue rows a typed search or *Search all* lists. */
export const IDENTIFY_SEARCH_LIMIT = 50;

export interface EquipmentIdentifyRender {
  renderGroupActions: (group: IdentifyGroup, rowIds: number[]) => React.ReactNode;
  openGroupKey: string | null;
  dock: React.ReactNode;
  /**
   * The ONE *Not Tracked* action, for the group header's button as well as the card (review:
   * failure paths, frontend) — one in-flight guard per group across both, one set of toasts.
   */
  notTracked: {
    mark: (
      group: IdentifyGroup,
      rowIds: number[],
      onLanded?: (origin: Element | null) => void,
    ) => Promise<boolean>;
    busy: ReadonlySet<string>;
  };
  /**
   * The selected group to confirm (`equipment-page-confirm`, R6): its header wears the selected
   * look while no card is open. Null outside the Groups view and while nothing is to confirm.
   */
  selectedGroupKey: string | null;
  /** Select a group to confirm — focusing or clicking in its header does this (R6). */
  selectGroup: (key: string) => void;
  /** *Confirm All {n}* for the panel bar (R7); null while nothing shown is to confirm. */
  barActions: React.ReactNode;
}

export interface EquipmentIdentifyProps {
  /** Null while no twin is open: nothing is offered. */
  twinId: number | null;
  /** Null while the register cannot be counted: nothing is offered (the Groups view needs it too). */
  model: IdentifyModel | null;
  /** The rows the chip and the search allow; undefined is every row. */
  visibleIds: ReadonlySet<number> | undefined;
  view: 'rows' | 'groups';
  /**
   * The step's chip. Not read: `visibleIds` already carries it, so a chip that hides the open
   * group's rows closes the card by the visibility rule, and a plan chip that shows groups still
   * to identify keeps the card available (Copilot review of #2087, round 2).
   */
  chip: EquipmentChip;
  baseUrl: string;
  getToken: () => Promise<string>;
  /** The twin on screen now — a write issued for another twin is refused, never sent. */
  currentTwinId: () => number | null;
  reloadRegister: () => void;
  beginRegisterWrite: () => number | null;
  settleRegisterWrite: (
    token: number | null,
    outcome: { ok: boolean; reason?: string | null },
  ) => void;
  markNotTracked?: MarkNotTracked;
  children: (render: EquipmentIdentifyRender) => React.ReactNode;
}

/** A catalogue row as a picker choice: *Life Fitness · ICG* beneath the product's name. */
export const toChoice = (row: ManufacturerHierarchy): IdentifyChoice => ({
  referenceId: row.entity_bid,
  product: row.model_name,
  meta:
    [row.manufacturer_name, row.product_line].filter(part => part && part.trim()).join(' · ') ||
    null,
});

/**
 * A generic product as a choice. Generic products share names ("Abdominal" four times), so the
 * catalogue's equipment type is the second line that tells them apart.
 */
export const toGenericChoice = (row: ManufacturerHierarchy): IdentifyChoice => ({
  referenceId: row.entity_bid,
  product: row.model_name,
  meta: row.equipment_type?.trim() || row.equipment_subcategory?.trim() || null,
});

/**
 * Wait for `work`, but give up when `signal` aborts (the read itself takes no signal). The work's
 * handlers are attached first, even when the signal has already aborted, so a read that fails
 * after being given up is still consumed, never an unhandled rejection (Copilot review of #2087).
 */
export const unlessAborted = <T,>(work: Promise<T>, signal: AbortSignal): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const abort = () => reject(Object.assign(new Error('aborted'), { name: 'AbortError' }));
    work.then(resolve, reject).finally(() => signal.removeEventListener('abort', abort));
    if (signal.aborted) {
      abort();
      return;
    }
    signal.addEventListener('abort', abort, { once: true });
  });

/**
 * The identify card's service binding for one twin's register (`equipment-page-identify`, shared by
 * `floor-plan-page-identify`): the bulk identify, Undo, the guesses' refresh and the register re-read,
 * each refused when another twin is on screen now, and the picker's catalogue reads. One binding,
 * so the Equipment step and the Floor Plan step write the same way.
 *
 * `settling` is the host's map of groups a write has just landed on; an Undo that restores rows
 * releases the groups holding them.
 */
export const useRegisterIdentifyBinding = ({
  twinId,
  baseUrl,
  getToken,
  currentTwinId,
  reloadRegister,
  beginRegisterWrite,
  settleRegisterWrite,
  settling,
}: {
  twinId: number | null;
  baseUrl: string;
  getToken: () => Promise<string>;
  currentTwinId: () => number | null;
  reloadRegister: () => void;
  beginRegisterWrite: () => number | null;
  settleRegisterWrite: (
    token: number | null,
    outcome: { ok: boolean; reason?: string | null },
  ) => void;
  settling: { readonly current: Map<string, readonly number[]> };
}): { api: IdentifyGroupApi; fetchers: IdentifyPickerFetchers } => {
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  /** The twin a write is for — refused when another twin is on screen now. */
  const issuedFor = useCallback((): number => {
    if (twinId === null || currentTwinId() !== twinId) {
      throw Object.assign(new Error('The twin changed; nothing was sent.'), { stale: true });
    }
    return twinId;
  }, [currentTwinId, twinId]);

  const api = useMemo<IdentifyGroupApi>(
    () => ({
      identify: async (referenceId, assetIds, key) => {
        const twin = issuedFor();
        const token = await getTokenRef.current();
        issuedFor();
        const result = await identifyTwinAssets(
          baseUrl,
          token,
          { twinId: twin, referenceId, assetIds },
          key,
        );
        // Before the re-read, as the PATCH path does: a row added this session is otherwise held
        // by the store as it was created, and re-read unidentified (Copilot, PR #2177).
        useEquipmentCatalogStore.getState().forgetRegisterWrites(assetIds);
        return result;
      },
      undo: async (result, key) => {
        const twin = issuedFor();
        const token = await getTokenRef.current();
        issuedFor();
        const answer = await undoIdentifyTwinAssets(baseUrl, token, twin, result, key);
        // Restored rows are to identify again: a group held back for them is offered again, even
        // where the Undo's re-read lands before the identify's (Copilot review of #2087, round 3).
        const restored = new Set([
          ...answer.restored.map(asset => Number(asset.id)),
          ...answer.alreadyRestored.map(Number),
        ]);
        settling.current.forEach((rows, groupKey) => {
          if (rows.some(id => restored.has(id))) settling.current.delete(groupKey);
        });
        const forget: number[] = [];
        restored.forEach(id => forget.push(id));
        useEquipmentCatalogStore.getState().forgetRegisterWrites(forget);
        return answer;
      },
      refresh: async () => {
        const twin = issuedFor();
        const token = await getTokenRef.current();
        issuedFor();
        return refreshTwinAssetProposals(baseUrl, token, { twinId: twin });
      },
      reload: () => {
        if (twinId !== null && currentTwinId() === twinId) reloadRegister();
      },
      beginWrite: beginRegisterWrite,
      settleWrite: (token, ok, reason) =>
        settleRegisterWrite(token, { ok, reason: reason ?? null }),
      logContext: twinId !== null ? { twinId } : undefined,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `settling` is a ref read at call time; its identity never matters
    [
      baseUrl,
      twinId,
      issuedFor,
      currentTwinId,
      reloadRegister,
      beginRegisterWrite,
      settleRegisterWrite,
    ],
  );

  const fetchers = useMemo<IdentifyPickerFetchers>(
    () => ({
      ranked: async (assetId, signal, manufacturer) => {
        const token = await getTokenRef.current();
        return getRankedCandidates(baseUrl, token, assetId, signal, manufacturer);
      },
      search: async (query, signal) => {
        const token = await getTokenRef.current();
        const found = await unlessAborted(
          findManufacturerCatalog(baseUrl, token, { q: query, limit: IDENTIFY_SEARCH_LIMIT }),
          signal,
        );
        return found.rows.map(toChoice);
      },
      generics: async signal => {
        const token = await getTokenRef.current();
        const rows = await unlessAborted(
          getManufacturerProductsByManufacturer(baseUrl, token, GENERIC_CATALOGUE_MANUFACTURER),
          signal,
        );
        return rows.map(toGenericChoice);
      },
    }),
    [baseUrl],
  );

  return { api, fetchers };
};

/** Which state a card was opened from: *Identify* on To identify, *Change* on To confirm (R12). */
type CardFrom = 'to_identify' | 'to_confirm';

/** Where a group stood when an action began: its key and its place in the model. */
interface GroupAt {
  key: string;
  index: number;
}

type GroupProposal = NonNullable<IdentifyGroup['proposal']>;

/** A group to confirm that can be confirmed: it carries a guess to write. */
const confirmable = (group: IdentifyGroup): boolean =>
  group.state === 'to_confirm' && Boolean(group.proposal?.reference_id);

export const EquipmentIdentify: React.FC<EquipmentIdentifyProps> = ({
  twinId,
  model,
  visibleIds,
  view,
  baseUrl,
  getToken,
  currentTwinId,
  reloadRegister,
  beginRegisterWrite,
  settleRegisterWrite,
  markNotTracked,
  children,
}) => {
  /** The group the card is open on, where it stood in the model, and the state it was opened from. */
  const [open, setOpen] = useState<
    | (GroupAt & {
        from: CardFrom;
        /** The card's own identity: its key when opened, kept if the group is re-keyed. */
        cardKey: string;
        /** One of the group's rows, to find it again under a new key (R11). */
        row: number | null;
      })
    | null
  >(null);
  // Read when a write lands: a result moves the card only if it is still on that group.
  const openRef = useRef(open);
  openRef.current = open;
  const identifyButtons = useRef(new Map<string, HTMLButtonElement>());
  const confirmButtons = useRef(new Map<string, HTMLButtonElement>());
  const changeButtons = useRef(new Map<string, HTMLButtonElement>());
  const modelRef = useRef<IdentifyModel>(model ?? EMPTY_MODEL);
  modelRef.current = model ?? EMPTY_MODEL;
  /** The selected group to confirm (`equipment-page-confirm`, R6) — where Enter on Confirm acts. */
  const [selected, setSelected] = useState<string | null>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  // A new twin starts closed, with nothing selected.
  useEffect(() => {
    setOpen(null);
    setSelected(null);
  }, [twinId]);

  /** A group's rows the chip and search allow — what an action on the group acts on. */
  const visibleRows = useCallback(
    (group: IdentifyGroup) =>
      visibleIds ? group.rowIds.filter(id => visibleIds.has(id)) : group.rowIds,
    [visibleIds],
  );

  const groups = model?.groups ?? EMPTY_MODEL.groups;
  /*
   * Groups a write has just landed on, with the rows it wrote, until the register re-reads them.
   * Moving on skips them, so identifying A then B before the reload lands never wraps back to A.
   * A group is released once any row written has left the card's states (To identify, To confirm)
   * in the model — the re-read carrying the write has landed — so a group a search showed only part
   * of is offered again for the rest, and an Undo can put a group back (Copilot review of #2087,
   * rounds 1 and 2). Both states, as Floor Plan's host releases them: a confirm writes To confirm
   * rows, and neither write leaves a row in the other state (`equipment-page-confirm`, R12).
   */
  const settling = useRef(new Map<string, readonly number[]>());
  useEffect(() => {
    if (!model) return;
    settling.current.forEach((rows, key) => {
      if (rows.some(id => !opensIdentifyCard(model.stateOf(id)))) settling.current.delete(key);
    });
  }, [model]);
  const toIdentify = useMemo(
    () => groups.filter(group => group.state === 'to_identify' && visibleRows(group).length > 0),
    [groups, visibleRows],
  );
  /*
   * The group the card is open on. A card opened by *Change* is found by its rows when a guess
   * refresh has re-keyed the group (a To confirm group's key is its guess) — during render, so the
   * card is never unmounted for a frame and keeps what the operator typed (Copilot review of #2171).
   */
  const openGroup = open
    ? (groups.find(group => group.key === open.key) ??
      (open.from === 'to_confirm' && open.row !== null
        ? (groups.find(g => g.state === 'to_confirm' && g.rowIds.includes(open.row as number)) ??
          null)
        : null))
    : null;

  /*
   * Close rules (R4): the card belongs to the Groups view of groups still in the state it was
   * opened from. Switching to Rows, or a chip or search that hides the open group's rows, closes it.
   * A group that has just been written also leaves that state; that is handled by moving on (R15),
   * which runs before this can see it, so it never closes the card under the operator.
   */
  const offered = model !== null && twinId !== null && view === 'groups';
  useEffect(() => {
    if (!open) return;
    if (!offered) setOpen(null);
    else if (openGroup && visibleRows(openGroup).length === 0) setOpen(null);
    // Settled some other way — the header's Not Tracked, another editor, a toast's Try again —
    // the group has left the state the card was opened from (or the model): move on as a write of
    // the card's own would (review: correctness, frontend, failure paths; `equipment-page-confirm`,
    // R12 — a card opened by *Change* is not closed for being To confirm).
    // A card opened by *Change* follows its rows when a guess refresh re-keys the group (a To
    // confirm group's key is its guess) — the operator is mid-pick (review: correctness).
    // Re-keyed under the card: keep its key in step with the group it found (above).
    else if (openGroup && openGroup.key !== open.key) setOpen({ ...open, key: openGroup.key });
    else if (!openGroup || openGroup.state !== open.from) settledRef.current(open);
  }, [open, offered, openGroup, visibleRows, groups]);

  const focusAfterClose = useCallback((groupKey: string | null, from: CardFrom = 'to_identify') => {
    window.setTimeout(() => {
      const buttons = from === 'to_confirm' ? changeButtons : identifyButtons;
      const button = groupKey ? buttons.current.get(groupKey) : undefined;
      (button?.isConnected ? button : document.getElementById('equipment-main-title'))?.focus();
    }, 0);
  }, []);

  const close = useCallback(() => {
    const key = open?.key ?? null;
    setOpen(null);
    focusAfterClose(key, open?.from);
  }, [open, focusAfterClose]);

  const openOn = useCallback((group: IdentifyGroup, from: CardFrom = 'to_identify') => {
    const index = modelRef.current.groups.findIndex(g => g.key === group.key);
    setOpen({
      key: group.key,
      index: Math.max(0, index),
      from,
      cardKey: group.key,
      row: group.rowIds[0] ?? null,
    });
  }, []);

  /** Moving on (R15): the next group to identify after this one, wrapping; none closes the card. */
  const moveOn = useCallback(
    (from: GroupAt, afterWrite: boolean, written?: readonly number[]) => {
      const all = modelRef.current.groups;
      const shown = (group: IdentifyGroup) =>
        group.state !== 'to_identify' || visibleRows(group).length > 0;
      if (written && written.length > 0) settling.current.set(from.key, written);
      const groups = all.filter(
        g => shown(g) && (g.key === from.key || !settling.current.has(g.key)),
      );
      // The group's place in THIS list — the one searched below — not in the model's: a hidden
      // group before it would otherwise push the start past its neighbour (review: correctness).
      const at = groups.findIndex(g => g.key === from.key);
      const index = at >= 0 ? at : all.slice(0, from.index).filter(shown).length;
      // After a write the group is on its way out of To identify: never offer it again.
      const next = nextGroupToIdentify(
        afterWrite ? groups.filter(g => g.key !== from.key) : groups,
        { key: from.key, index },
      );
      if (!next) {
        setOpen(null);
        focusAfterClose(null);
        return;
      }
      openOn(next);
    },
    [openOn, visibleRows, focusAfterClose],
  );
  /** A write that landed moves the card on only if the card is still on the group it was for. */
  const movedFrom = useCallback(
    (from: GroupAt, written: readonly number[]) => {
      if (openRef.current?.key === from.key) moveOn(from, true, written);
    },
    [moveOn],
  );

  /**
   * The groups to confirm on screen now, less those a write has just landed on (R6, R7) — by ROW,
   * not only by key: a group's key is its guess, so a guess refresh read before a write lands can
   * show the same rows under a new key, and offering them again would let the next Enter write the
   * re-ranked guess over the one just confirmed (review: failure paths).
   */
  /** Every row a write has just landed on, whatever group it sits in now. */
  const settlingRows = (): Set<number> => {
    const rows = new Set<number>();
    settling.current.forEach(ids => ids.forEach(id => rows.add(id)));
    return rows;
  };

  /**
   * A group's rows a confirm may write: those shown, less any a write has just landed on — a
   * re-keyed group can mix them with untouched rows, and they must never be sent again under a
   * different guess (Copilot review of #2171).
   */
  const actionableRows = useCallback(
    (group: IdentifyGroup, written: ReadonlySet<number> = settlingRows()) =>
      visibleRows(group).filter(id => !written.has(id)),
    // `settlingRows` reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRows],
  );

  const confirmableShown = useCallback(
    (keep?: string) => {
      const written = settlingRows();
      return modelRef.current.groups.filter(g => {
        if (!confirmable(g) || visibleRows(g).length === 0) return false;
        // The group just written keeps its place, so the next one is found after it.
        if (g.key === keep) return true;
        return !settling.current.has(g.key) && actionableRows(g, written).length > 0;
      });
    },
    // `settlingRows` reads a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleRows, actionableRows],
  );

  /** A group's *Confirm*, or the panel title when there is none — focused now. */
  const focusConfirmNow = useCallback((key: string | null) => {
    const button = key ? confirmButtons.current.get(key) : undefined;
    (button?.isConnected ? button : document.getElementById('equipment-main-title'))?.focus();
  }, []);

  /**
   * The same, after this tick — but only from where the press left the focus (`origin`, or the
   * body), never from somewhere the operator has since gone (the Groups view's `moveFocusPast`).
   */
  const focusConfirmFrom = useCallback(
    (key: string | null, origin: Element | null) => {
      window.setTimeout(() => {
        const active = document.activeElement;
        if (active && active !== document.body && active !== origin) return;
        focusConfirmNow(key);
      }, 0);
    },
    [focusConfirmNow],
  );

  /** The rows of a failed confirm still to confirm now — what a toast's *Try again* sends. */
  const stillToConfirm = useCallback(
    (failed: readonly number[]) =>
      failed.filter(row => modelRef.current.stateOf(row) === 'to_confirm'),
    [],
  );

  /**
   * After a confirm — or a card opened by *Change* closing (`equipment-page-confirm`, R6, R11):
   * select the next group to confirm after this one, wrapping, and give its *Confirm* the focus;
   * none left gives the panel's title the focus. Focus moves only from where the press left it —
   * the pressed button, or the body after a card closed — never from somewhere the operator has
   * since gone (the Groups view's rule, `moveFocusPast`).
   */
  const confirmMovedOn = useCallback(
    (
      from: GroupAt,
      written: readonly number[],
      origin: Element | null,
      /** Called as a card closes: the focus moves once it has gone (see `focusAfterCard`). */
      cardClosing = false,
    ) => {
      if (written.length > 0) settling.current.set(from.key, written);
      const all = modelRef.current.groups;
      const list = confirmableShown(from.key);
      const at = list.findIndex(g => g.key === from.key);
      const index =
        at >= 0
          ? at
          : all.slice(0, from.index).filter(g => confirmable(g) && visibleRows(g).length > 0)
              .length;
      const next = nextGroupToIdentify(
        list.filter(g => g.key !== from.key),
        { key: from.key, index },
        ['to_confirm'],
      );
      setSelected(next?.key ?? null);
      if (cardClosing) {
        focusAfterCard.current = next?.key ?? '';
        return;
      }
      focusConfirmFrom(next?.key ?? null, origin);
    },
    [confirmableShown, visibleRows, focusConfirmFrom],
  );

  /** A card opened by *Change* closes, and the next guess is selected (R11, ruled 2026-10-20). */
  const closeToNextGuess = useCallback(
    (from: GroupAt, written: readonly number[]) => {
      if (openRef.current?.key !== from.key) {
        // The card has moved on or closed already; the selection still moves past the group.
        if (written.length > 0) settling.current.set(from.key, written);
        return;
      }
      setOpen(null);
      confirmMovedOn(from, written, null, true);
    },
    [confirmMovedOn],
  );

  /*
   * Where the focus goes once a card opened by *Change* has closed (R11): the next guess's
   * *Confirm*, or the panel title ('') when none is left. Moved by an effect AFTER the close has
   * committed — the card hands the focus back to the group's *Change* as it unmounts, and that
   * button then leaves with the confirmed group, which dropped the focus to the body (found in the
   * real-app A/B, 2026-10-20).
   */
  const focusAfterCard = useRef<string | null>(null);
  useEffect(() => {
    if (open !== null || focusAfterCard.current === null) return;
    const key = focusAfterCard.current;
    focusAfterCard.current = null;
    focusConfirmNow(key || null);
  }, [open, focusConfirmNow]);

  /** The card's group has left the state it was opened from some other way (the close rule). */
  const settled = useCallback(
    (at: GroupAt & { from: CardFrom }) => {
      if (at.from === 'to_confirm') closeToNextGuess(at, []);
      else moveOn(at, true);
    },
    [closeToNextGuess, moveOn],
  );
  const settledRef = useRef(settled);
  settledRef.current = settled;

  const { api, fetchers } = useRegisterIdentifyBinding({
    twinId,
    baseUrl,
    getToken,
    currentTwinId,
    reloadRegister,
    beginRegisterWrite,
    settleRegisterWrite,
    settling,
  });
  const writes = useIdentifyGroup(api);
  const notTracked = useNotTrackedAction({
    onMarkNotTracked: markNotTracked,
    stateOf: row => modelRef.current.stateOf(row),
  });

  /*
   * The selection (R6): exactly one group to confirm among those shown, in the Groups view. On
   * landing, or when the selected one is no longer shown, the first — and no focus is taken. Where
   * the selected group went while its *Confirm* held the focus (a guess refresh re-keys a group,
   * whose key is its guess), the newly selected group's *Confirm* takes it, so Enter still works.
   */
  const shownKeys = offered ? confirmableShown(selected ?? undefined).map(g => g.key) : [];
  const shownSignature = shownKeys.join('\n');
  useEffect(() => {
    const current = selectedRef.current;
    if (current !== null && shownKeys.includes(current)) return;
    const next = shownKeys[0] ?? null;
    setSelected(next);
    if (current === null || next === null) return;
    window.setTimeout(() => {
      if (document.activeElement && document.activeElement !== document.body) return;
      const button = confirmButtons.current.get(next);
      if (button?.isConnected) button.focus();
    }, 0);
    // The keys' signature is the dependency: a new array each render must not re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownSignature]);

  const selectGroup = useCallback(
    (key: string) => {
      if (confirmableShown().some(g => g.key === key)) setSelected(key);
    },
    [confirmableShown],
  );

  /*
   * Which settling groups a Brand confirm wrote — the only writes that move the score. Kept beside
   * `settling` rather than in it, so an Undo that releases a group (the binding deletes it from
   * `settling`) drops it from the projection too (review: correctness).
   */
  const brandSettling = useRef(new Set<string>());

  /** Brand rows written by earlier presses that the model does not yet show as identified. */
  const pendingRows = (): number => {
    let pending = 0;
    brandSettling.current.forEach(key => {
      const rows = settling.current.get(key);
      if (!rows) {
        brandSettling.current.delete(key);
        return;
      }
      pending += rows.filter(id => opensIdentifyCard(modelRef.current.stateOf(id))).length;
    });
    return pending;
  };
  const isBrandGuess = (group: IdentifyGroup) =>
    !isGenericCatalogueManufacturer(group.proposal?.manufacturer?.trim());

  /**
   * The confirm toast's second line (R9): *Hammer Strength · 81 % identified*. The score is the
   * one the register's re-read will show — projected, as Floor Plan's toast does
   * (`projectedScore`): the toast is raised before that read lands. A Generic guess does not move
   * the score (Generic is never in the share).
   */
  const scoreAfter = (brandWritten: number): number | null => {
    const score = modelRef.current.score;
    if (!score) return null;
    return projectedScore(score, pendingRows() + brandWritten).after;
  };
  const confirmSub = (proposal: GroupProposal, written: number): string => {
    const generic = isGenericCatalogueManufacturer(proposal.manufacturer?.trim());
    const percent = scoreAfter(generic ? 0 : written);
    const maker = guessMaker(proposal) ?? proposal.product;
    return percent === null ? maker : `${maker} · ${percent} % identified`;
  };

  /** Confirm (R5): the group's rows become the guess shown — the shown product wins. */
  const confirm = useCallback(
    (group: IdentifyGroup, shownRows: number[]) => {
      const proposal = group.proposal;
      if (!proposal?.reference_id) return;
      const written = settlingRows();
      const rowIds = shownRows.filter(id => !written.has(id));
      if (rowIds.length === 0) return;
      const origin = document.activeElement;
      const at: GroupAt = {
        key: group.key,
        index: Math.max(
          0,
          modelRef.current.groups.findIndex(g => g.key === group.key),
        ),
      };
      setSelected(group.key);
      const landedOn = (from: Element | null) => {
        confirmMovedOn(at, rowIds, from);
        if (isBrandGuess(group)) brandSettling.current.add(group.key);
      };
      void writes
        .identify(
          group,
          proposal.reference_id,
          rowIds,
          stillToConfirm,
          // A toast's Try again that succeeds moves on too, from wherever the focus is then.
          () => landedOn(null),
          undefined,
          { verb: 'confirmed', sub: count => confirmSub(proposal, count) },
        )
        .then(outcome => {
          if (outcome === 'moved') landedOn(origin);
        });
    },
    // `confirmSub` reads refs only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [writes.identify, confirmMovedOn, stillToConfirm],
  );

  /** *Confirm All* (R7): exactly the groups to confirm on screen, one after another. */
  const confirmAll = () => {
    const list = confirmableShown();
    if (list.length === 0) return;
    const origin = document.activeElement;
    const landed = (answer: IdentifyBatchAnswer, from: Element | null) => {
      if (answer.done.length === 0) return;
      // `group` is the model's group each entry was built from (spread copies keep the reference).
      answer.done.forEach(entry => {
        const group = entry.group as IdentifyGroup;
        // An entry with no rows wrote nothing; recorded, it could never be released.
        if (entry.rowIds.length === 0) return;
        settling.current.set(group.key, entry.rowIds);
        if (isBrandGuess(group)) brandSettling.current.add(group.key);
      });
      const next = confirmableShown()[0] ?? null;
      setSelected(next?.key ?? null);
      focusConfirmFrom(next?.key ?? null, from);
    };
    void writes
      .identifyBatch(
        list.map(group => ({
          group,
          referenceId: group.proposal!.reference_id,
          rowIds: actionableRows(group),
          retry: stillToConfirm,
        })),
        {
          verb: 'confirmed',
          sub: (_written, results) => {
            // The Brand rows THIS attempt wrote: a retry is a smaller batch, and Generic rows never
            // move the share (Copilot review of #2171).
            const brandWritten = results
              .filter(r => !isGenericCatalogueManufacturer(r.product.manufacturer?.trim()))
              .reduce((n, r) => n + r.identified.length, 0);
            const percent = scoreAfter(brandWritten);
            return percent === null ? null : `${percent} % identified`;
          },
          onRetried: answer => landed(answer, null),
        },
      )
      .then(answer => landed(answer, origin));
  };

  const renderGroupActions = useCallback(
    (group: IdentifyGroup, rowIds: number[]) => {
      if (confirmable(group)) {
        const proposal = group.proposal!;
        // *Confirm Chest press × 4 as Hammer Strength Iso-Lateral Chest Press* (Block A).
        const product = guessText(proposal).replace(' · ', ' ');
        return (
          <React.Fragment key="confirm">
            <Button
              key="change"
              ref={element => {
                if (element) changeButtons.current.set(group.key, element);
                else changeButtons.current.delete(group.key);
              }}
              surface="on-glass"
              size="sm"
              variant="text"
              iconLeft={<Icon name="pen" />}
              // Busy with every other group action while a write runs (R7, Failure States): a
              // card opened mid-write would be closed under the operator when the write lands.
              loading={writes.busy}
              aria-label={`Change guess for ${group.label} × ${rowIds.length}`}
              aria-expanded={open?.key === group.key}
              onClick={() => {
                setSelected(group.key);
                openOn(group, 'to_confirm');
              }}
            >
              Change
            </Button>
            <Button
              key="confirm"
              ref={element => {
                if (element) confirmButtons.current.set(group.key, element);
                else confirmButtons.current.delete(group.key);
              }}
              surface="on-glass"
              size="sm"
              variant="frosted"
              iconLeft={<Icon name="check" />}
              /* `loading` alone: it already refuses a second press, and a `disabled` button
                 would drop the focus it holds to the page body (review, A2). */
              loading={writes.busy}
              aria-label={`Confirm ${group.label} × ${rowIds.length} as ${product}`}
              onClick={() => confirm(group, rowIds)}
            >
              Confirm
            </Button>
          </React.Fragment>
        );
      }
      if (group.state !== 'to_identify') return null;
      const suggested = suggestsNotTracked(group.written.category);
      return (
        <Button
          key="identify"
          ref={element => {
            if (element) identifyButtons.current.set(group.key, element);
            else identifyButtons.current.delete(group.key);
          }}
          surface="on-glass"
          size="sm"
          variant={suggested ? 'text' : 'frosted'}
          iconLeft={<Icon name="magnifying-glass" />}
          loading={writes.busy}
          // The spec's name (Block A): *Identify {label} × {count}*.
          aria-label={`Identify ${group.label} × ${rowIds.length}`}
          aria-expanded={open?.key === group.key}
          onClick={() => openOn(group)}
        >
          Identify
        </Button>
      );
    },
    [open, openOn, confirm, writes.busy],
  );

  let dock: React.ReactNode = null;
  if (open && openGroup && offered) {
    const rowIds = visibleRows(openGroup);
    const from = open;
    const guessed = from.from === 'to_confirm';
    const position = toIdentify.findIndex(group => group.key === openGroup.key);
    // Where the card goes once its write lands: on through the groups to identify, or — opened
    // by *Change* — closed, with the next guess selected (R11).
    const landed = (written: readonly number[]) =>
      guessed ? closeToNextGuess(from, written) : movedFrom(from, written);
    dock = (
      <IdentifyCard
        // Per group: every group starts on Brand with its own ranked choices.
        key={open.cardKey}
        group={{
          key: openGroup.key,
          label: openGroup.label,
          count: rowIds.length,
          sampleAssetId: rowIds[0] ?? openGroup.rowIds[0],
          written: {
            category: openGroup.written.category,
            manufacturer: openGroup.written.manufacturer,
          },
          // The guess on top (R11); the card's sub then reads *guessed*.
          proposal: guessed ? openGroup.proposal : null,
        }}
        position={{ n: position >= 0 ? position + 1 : 1, of: Math.max(toIdentify.length, 1) }}
        fetchers={fetchers}
        logContext={twinId !== null ? { twinId } : undefined}
        busy={writes.busy || notTracked.busy.has(openGroup.key)}
        onIdentify={referenceId => {
          void writes
            .identify(
              openGroup,
              referenceId,
              rowIds,
              failed => failed.filter(row => modelRef.current.stateOf(row) === from.from),
              // A toast's Try again that succeeds moves on too.
              () => landed(rowIds),
            )
            .then(outcome => {
              if (outcome === 'moved') landed(rowIds);
            });
        }}
        onMarkNotTracked={() => {
          void notTracked.mark(openGroup, rowIds).then(done => {
            if (done) landed(rowIds);
          });
        }}
        onSkip={() => (guessed ? closeToNextGuess(from, []) : moveOn(from, false))}
        onClose={close}
      />
    );
  }

  const shownCount = offered
    ? confirmableShown().reduce((n, group) => n + actionableRows(group).length, 0)
    : 0;
  const barActions =
    offered && !dock && shownCount > 0 ? (
      <Button
        variant="primary"
        size="sm"
        iconLeft={<Icon name="check-double" />}
        loading={writes.busy}
        aria-label={`Confirm all ${shownCount} guesses`}
        onClick={confirmAll}
      >
        Confirm All {shownCount}
      </Button>
    ) : null;

  return (
    <>
      {children({
        renderGroupActions,
        openGroupKey: dock ? (open?.key ?? null) : null,
        dock,
        notTracked,
        selectedGroupKey: offered ? selected : null,
        selectGroup,
        barActions,
      })}
    </>
  );
};

export default EquipmentIdentify;
